import React, { useCallback, useEffect, useState } from "react";
import Layout from "../../layouts/default";
import { Button, Spinner, Table, Form } from "react-bootstrap";
import {
  fetchGlobalSettings,
  updateGlobalSetting,
} from "../../services/globalSettings";
import { toast } from "react-toastify";
import { hasPermission } from "../../services/utils";
import { PERMISSIONS } from "../../contants/permission";

const optionLabel = (setting, value) =>
  setting.options?.find((o) => o.value === value)?.label || value;

function GlobalSettings() {
  const [list, setList] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState(null);

  const canEdit = hasPermission(PERMISSIONS.GLOBAL_SETTING.EDIT);
  const userId = () => JSON.parse(localStorage.getItem("user"))?.data?.id;

  const load = useCallback(async () => {
    setLoading(true);
    const res = await fetchGlobalSettings();
    setLoading(false);
    if (!res?.success) {
      toast.error(res?.message || "Failed to load global settings");
      return;
    }
    const records = res.data?.records || [];
    setList(records);
    setDrafts(Object.fromEntries(records.map((s) => [s.key, s.value])));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async (setting) => {
    const value = drafts[setting.key];
    setSavingKey(setting.key);
    const res = await updateGlobalSetting({
      key: setting.key,
      value,
      updatedBy: userId(),
    });
    setSavingKey(null);
    if (!res?.success) return toast.error(res?.message || "Failed to save");
    toast.success(res.message || "Setting updated");
    load();
  };

  return (
    <Layout>
      <div className="d-flex justify-content-between align-items-center my-3 px-3">
        <h4 className="fw-semibold mb-0">Global Settings</h4>
      </div>

      <div className="mx-3">
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
            <p>Loading...</p>
          </div>
        ) : list?.length ? (
          <Table bordered hover>
            <thead>
              <tr>
                <th>Setting</th>
                <th>Current Value</th>
                <th style={{ width: "30%" }}>Change To</th>
                <th>Last Updated</th>
                {canEdit && <th>Action</th>}
              </tr>
            </thead>
            <tbody>
              {list.map((s) => (
                <tr key={s.key}>
                  <td>
                    <div className="fw-semibold">{s.label}</div>
                    <small className="text-muted">{s.key}</small>
                  </td>
                  <td>
                    {optionLabel(s, s.value)}
                    {s.isDefault && (
                      <small className="text-muted"> (default)</small>
                    )}
                  </td>
                  <td>
                    <Form.Select
                      value={drafts[s.key] ?? ""}
                      disabled={!canEdit || savingKey === s.key}
                      onChange={(e) =>
                        setDrafts({ ...drafts, [s.key]: e.target.value })
                      }
                    >
                      {(s.options || []).map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </Form.Select>
                  </td>
                  <td>
                    {s.updatedAt ? new Date(s.updatedAt).toLocaleString() : "-"}
                  </td>
                  {canEdit && (
                    <td>
                      <Button
                        size="sm"
                        disabled={
                          savingKey === s.key ||
                          (drafts[s.key] === s.value && !s.isDefault)
                        }
                        onClick={() => handleSave(s)}
                      >
                        {savingKey === s.key ? "Saving..." : "Save"}
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <p className="text-center py-5">No settings found</p>
        )}
      </div>
    </Layout>
  );
}

export default GlobalSettings;
